/**
 * @typedef {{
 *   title: string;
 *   board_id: number;
 * }} Board
 */
import groupRecordsByPinyin from "@/utils/groupRecordsByPinyin";
import { getPossiblePinYin } from "@/utils/str";
import { useCallback, useEffect, useState } from "react";
import HBIcon from "../HBIcon";

/**
 * @param {Object} config
 * @param {Board[]} config.boards
 * @param {string} config.keyword
 * @param {'multiple'|undefined} config.mode
 * @param {number[]} config.value
 * @param {(selected: number[], selectedBoards: Board[]) => void} config.onSelected
 * @returns {{
 *    boardsToRender: [
 *      {
 *        title: ReactNode,
 *        boards: Board[]
 *      }
 *    ];
 *    canItemShow?: (board: any) => boolean;
 *    selected: number[];
 *    onBoardSelect: (board: Board) => () => void;
 *  }}
 */
export default function useMyBoardFilter(config) {
  const {
    boards,
    keyword,
    onSelected,
    mode,
    value,
    canItemShow,
    selectable,
    onFilter,
    onlyCreation,
    onBoardsUpdate,
    onBoardClick: _onBoardClick,
  } = config;
  const [selected, setSelected] = useState(value ?? []);
  const [boardsToRender, setBoardsToRender] = useState([]);

  const calculateBoardsToRender = useCallback(async () => {
    const tboards = boards ?? [];
    const _boards = canItemShow
      ? (tboards ?? [])
          .filter((r) => (onlyCreation ? !!r.extra?.is_creation : true))
          .filter(canItemShow)
      : tboards.filter((r) => (onlyCreation ? !!r.extra?.is_creation : true));
    if (keyword) {
      let filteredBoards = [];
      for (const board of _boards) {
        const key = keyword.toLowerCase();

        const isTitleMatch = board.title.toLowerCase().includes(key);
        const isIdMatch = board.board_id.toString() === key;

        // 拼音匹配
        const availStrs = await getPossiblePinYin(board.title);
        const isTitlePinYinMatch = availStrs
          .map((s) => s.join(""))
          .reduce((result, currentPinYin) => {
            if (result) return true;

            if (currentPinYin.startsWith(key)) return true;
          }, false);

        // 拼音首字母匹配
        const availAplhaStrs = availStrs.map((r) =>
          r.map((p) => p[0]).join("")
        );
        const isTitleFirstPinYinMatch = availAplhaStrs.find((s) =>
          s.includes(key)
        );
        if (
          isTitleMatch ||
          isIdMatch ||
          isTitlePinYinMatch ||
          isTitleFirstPinYinMatch
        ) {
          filteredBoards.push(board);
        }
      }

      onFilter &&
        requestAnimationFrame(() => onFilter(keyword, filteredBoards));

      onBoardsUpdate && onBoardsUpdate(filteredBoards);
      const result = filteredBoards.length
        ? [
            {
              title: <HBIcon type="ic_search" />,
              boards: filteredBoards,
            },
          ]
        : [];
      setBoardsToRender(result);
    } else {
      if (!_boards.length) return [];

      const RECENT_COUNT = 5;
      const boardsToParse = _boards.slice(RECENT_COUNT, _boards.length);
      const groups = await groupRecordsByPinyin(
        boardsToParse,
        (board) => board.title
      );
      const result = [
        {
          title: <HBIcon type="ic_schedule2" />,
          boards: _boards.slice(0, RECENT_COUNT),
        },
        ...groups.map(([title, boards]) => ({ title, boards })),
      ];

      let t = [];
      for (const { boards } of result) {
        t = t.concat(boards);
      }
      onBoardsUpdate && onBoardsUpdate(t);
      setBoardsToRender(result);
    }
  }, [boards, canItemShow, keyword, onlyCreation, onFilter, onBoardsUpdate]);
  useEffect(() => {
    calculateBoardsToRender();
  }, [calculateBoardsToRender]);

  const onBoardSelect = useCallback(
    (board, isLimit) => () => {
      _onBoardClick && _onBoardClick(board);
      if (!selectable) return;

      const { board_id } = board;

      if (!isLimit) {
        let result;
        if (selected.includes(board_id)) {
          result = selected.filter((id) => id !== board_id);
        } else {
          result = mode !== "multiple" ? [board_id] : [...selected, board_id];
        }
        onSelected &&
          onSelected(
            result,
            boards.filter((board) => result.includes(board.board_id))
          );
        setSelected(result);
      }
    },
    [onSelected, mode, selected, boards, selectable, _onBoardClick]
  );

  useEffect(() => {
    selectable && setSelected(value ?? []);
  }, [value, selectable]);

  return { ...config, boardsToRender, selected, onBoardSelect };
}
